import { Routes } from '@angular/router';
import { OktaCallbackComponent } from '@okta/okta-angular';

// Main
import { HomeScreenComponent } from './home/home-screen.component';

// List
import { AgeingListComponent } from './main/ageing/list/ageing-list.component';
import { AnnexureComponent } from './main/annexure/list/annexure-list.component';
import { AuditLogComponent } from './admin/audit-log/audit-log.component';
import { AuditLogResultComponent } from './admin/audit-log/audit-log-result/audit-log-result.component';
import { ClaimDataComponent } from './main/claim-data/list/claim-data.component';
import { ClientComponent } from './admin/client/client.component';
import { ExchangeRateComponent } from './admin/exchange-rate/exchange-rate.component';
import { MatchingComponent } from './main/matching/list/matching-list.component';
import { PerformanceComponent } from './main/performance/list/performance-list.component';
import { PolicyGroupComponent } from './admin/policy-group/policy-group.component';
import { PremiumDataComponent } from './main/premium-data/list/premium-data-list.component';
import { SalesListComponent } from './main/sales/list/sales-list.component';
import { TenantComponent } from './admin/tenant/tenant.component';
import { UserComponent } from './admin/user/user.component';
import { UserRoleComponent } from './admin/user-role/user-role.component';

// Create
import { CreateAgeingBatchComponent } from './main/ageing/create/create-ageing-batch.component';
import { CreateMatchingBatchComponent } from './main/matching/create/create-matching-batch.component';
import { CreateAnnexureBatchComponent } from './main/annexure/create/create-annexure-batch.component';
import { CreateClaimDataBatchComponent } from './main/claim-data/create/create-claim-data-batch.component';
import { CreateSalesBatchComponent } from './main/sales/create/create-sales-batch.component';
import { CreatePremiumDataBatchComponent } from './main/premium-data/create/create-premium-data-batch.component';
import { CreatePerformanceBatchComponent } from './main/performance/create/create-performance-batch.component';

// Create or Update
import { CreateOrUpdateClientComponent } from './admin/client/create-or-update-client.component';
import { CreateOrUpdateClientAreaComponent } from './admin/client/client-area/create-or-update-client-area.component';
import { CreateOrUpdateEndorsementComponent } from './admin/client/client-area/client-area-endorsement/create-or-update-endorsement.component';
import { CreateOrUpdateExchangeRateComponent } from './admin/exchange-rate/create-or-update-exchange-rate.component';
import { CreateOrUpdatePolicyGroupComponent } from './admin/policy-group/create-or-update-policy-group.component';
import { CreateOrUpdateTenantComponent } from './admin/tenant/create-or-update-tenant.component';
import { CreateOrUpdateUserComponent } from './admin/user/create-or-update-user.component';
import { CreateOrUpdateUserRoleComponent } from './admin/user-role/create-or-update-user-role.component';

// Reports
import { UserReportComponent } from './admin/user-report/user-report.component';
import { AgeingExportParametersComponent } from './main/declaration-reports/ageing-export/ageing-export-parameters.component';
import { AnnexureExportParametersComponent } from './main/declaration-reports/annexure-export/annexure-export-parameters.component';
import { SharedDebtorByPolicyParametersComponent } from './main/declaration-reports/shared-debtor-by-policy/shared-debtor-by-policy-parameters.component';
import { ReportViewerComponent } from './reports/report-viewer/report-viewer.component';
import { DataStatusReportParametersComponent } from './main/declaration-reports/data-status/data-status.component';
import { DeclarationReportSelectionComponent } from './main/declaration-reports/declaration-report-selection.component';
import { RenewalReportSelectionComponent } from './main/renewal-reports/renewal-report-selection.component';

// Wizards
import { AgeingWizardComponent } from './main/ageing/wizard/ageing-wizard.component';
import { AnnexureWizardComponent } from './main/annexure/wizard/annexure-wizard.component';
import { ClaimDataWizardComponent } from './main/claim-data/wizard/claim-data-wizard.component';
import { MatchingWizardComponent } from './main/matching/wizard/matching-wizard.component';
import { PerformanceWizardComponent } from './main/performance/wizard/performance-wizard.component';
import { PremiumDataWizardComponent } from './main/premium-data/wizard/premium-data-wizard.component';
import { SalesWizardComponent } from './main/sales/wizard/sales-wizard.component';

// Popi
import { PopiComponent } from './admin/popi/popi.component';

// PowerBi
import { PowerBiDashboardComponent } from './admin/power-bi/power-bi-dashboard/power-bi-dashboard.component';
import { PowerBiReportComponent } from './admin/power-bi/power-bi-report/power-bi-report.component';

// Health
import { HealthComponent } from './shared/components/health/health.component';

// Authentication
import { LoginCallbackResolver } from './shared/resolvers/login-callback-resolver';
import { LogoutCallbackResolver } from './shared/resolvers/logout-callback-resolver';
import { OktaRouteGuard } from './shared/guards/okta-route.guard';

// Constants
import { AreaConstants } from './shared/constants/area.constants';
import { PermissionConstants } from './shared/constants/permission.constants';
import { NotAuthenticatedComponent } from './shared/components/not-authenticated/not-authenticated.component';
import { NotAuthorizedComponent } from './shared/components/not-authorized/not-authorizedcomponent';
import { NotRegisteredComponent } from './shared/components/not-registered/not-registered.component';

export const routes: Routes = [
    {
        path: '',
        component: HomeScreenComponent
    },
    {
        path: 'LoginCallback',
        component: OktaCallbackComponent,
        resolve: {
            log: LoginCallbackResolver
        }
    },
    {
        path: 'LogoutCallback',
        redirectTo: '',
        resolve: {
            log: LogoutCallbackResolver
        }
    },
    {
        path: 'health',
        component: HealthComponent,
    },
    {
        path: 'not-authenticated',
        component: NotAuthenticatedComponent,
    },
    {
        path: 'not-authorized',
        component: NotAuthorizedComponent,
    },
    {
        path: 'not-registered',
        component: NotRegisteredComponent,
    },

    {
        path: 'ageing',
        component: AgeingListComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Ageing
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'ageing-create',
        component: CreateAgeingBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Ageing
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'ageing-export-parameters',
        component: AgeingExportParametersComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Ageing
            ],
            permissions: [
                PermissionConstants.Export
            ]
        }
    },
    {
        path: 'ageing-wizard/:id',
        component: AgeingWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Ageing
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'annexure',
        component: AnnexureComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Annexure
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'annexure-create',
        component: CreateAnnexureBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Annexure
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'annexure-export-parameters',
        component: AnnexureExportParametersComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Annexure
            ],
            permissions: [
                PermissionConstants.Export
            ]
        }
    },
    {
        path: 'annexure-wizard/:id',
        component: AnnexureWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Annexure
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'audit-log',
        component: AuditLogComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Auditing
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'audit-log-result/:id',
        component: AuditLogResultComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Auditing
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'claim-data',
        component: ClaimDataComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ClaimData
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'claim-data-create',
        component: CreateClaimDataBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ClaimData
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'claim-data-wizard/:id',
        component: ClaimDataWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ClaimData
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'client',
        component: ClientComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Client
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'client/:id',
        component: CreateOrUpdateClientComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Client
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'client-area/:clientId/:policyId',
        component: CreateOrUpdateClientAreaComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ClientArea
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'client-area/:clientId/:policyId/:endorsementId',
        component: CreateOrUpdateEndorsementComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ClientArea
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'data-status-report-parameters',
        component: DataStatusReportParametersComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.DeclarationReports
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'declaration-report-selection',
        component: DeclarationReportSelectionComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.DeclarationReports
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'exchange-rate',
        component: ExchangeRateComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ExchangeRate
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'exchange-rate/:id',
        component: CreateOrUpdateExchangeRateComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.ExchangeRate
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'matching',
        component: MatchingComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Matching
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'matching-create',
        component: CreateMatchingBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Matching
            ],
            permissions: [
                PermissionConstants.Matching
            ]
        }
    },
    {
        path: 'matching-wizard/:id',
        component: MatchingWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Matching
            ],
            permissions: [
                PermissionConstants.Matching
            ]
        }
    },
    {
        path: 'performance',
        component: PerformanceComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Performance
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'performance-create',
        component: CreatePerformanceBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Performance
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'performance-wizard/:id',
        component: PerformanceWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Performance
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'policy-group',
        component: PolicyGroupComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.PolicyGroup
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'policy-group/:id',
        component: CreateOrUpdatePolicyGroupComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.PolicyGroup
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'popi',
        component: PopiComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.PopiData
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'powerbi-dashboard',
        component: PowerBiDashboardComponent,
        data: {
            areas: [
                AreaConstants.PowerBi
            ],
            permissions: [
                PermissionConstants.Create
            ]
        }
    },
    {
        path: 'powerbi-report',
        component: PowerBiReportComponent,
        data: {
            areas: [
                AreaConstants.PowerBi
            ],
            permissions: [
                PermissionConstants.Create
            ]
        }
    },
    {
        path: 'premium-data',
        component: PremiumDataComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.PremiumData
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'premium-data-create',
        component: CreatePremiumDataBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.PremiumData
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'premium-data-wizard/:id',
        component: PremiumDataWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.PremiumData
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'report-viewer/:reportType',
        component: ReportViewerComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.DeclarationReports,
                AreaConstants.RenewalReports,
                AreaConstants.AdminReports
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'renewal-report-selection',
        component: RenewalReportSelectionComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.RenewalReports
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'sales',
        component: SalesListComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Sales
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'sales-create',
        component: CreateSalesBatchComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Sales
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'sales-wizard/:id',
        component: SalesWizardComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Sales
            ],
            permissions: [
                PermissionConstants.Import
            ]
        }
    },
    {
        path: 'shared-debtor-by-policy-parameters',
        component: SharedDebtorByPolicyParametersComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.DeclarationReports
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'tenant',
        component: TenantComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Tenant
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'tenant/:id',
        component: CreateOrUpdateTenantComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Tenant
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'user',
        component: UserComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.User
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'user/:id',
        component: CreateOrUpdateUserComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.User
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    },
    {
        path: 'user-report',
        component: UserReportComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.AdminReports
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'user-role',
        component: UserRoleComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Role
            ],
            permissions: [
                PermissionConstants.View
            ]
        }
    },
    {
        path: 'user-role/:id',
        component: CreateOrUpdateUserRoleComponent,
        canActivate: [
            OktaRouteGuard
        ],
        data: {
            areas: [
                AreaConstants.Role
            ],
            permissions: [
                PermissionConstants.View,
                PermissionConstants.Create,
                PermissionConstants.Edit
            ]
        }
    }
];
